import React, { useCallback, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { registriereZurueck } from '../../services/appUmfang';
import type { KatalogAuswahl, KatalogSprache } from '../../services/mangelKatalog/typen';
import { tasteDeuten } from './auswahlAnsicht';
import { MangelKatalogAuswahl } from './MangelKatalogAuswahl';
import type { KatalogSteuerung } from './MangelKatalogAuswahl';
import { katalogTexte } from './texte';

/**
 * Auswahl-Dialog für Desktop und Tablet-Layout („Standardmangel per Torte“,
 * Paket D): Modal mit Titel, Schließen-Knopf und der Auswahl in der
 * Darstellung 'dialog'.
 *
 * Tastatur: Esc geht einen Schritt zurück, in der Wurzel schließt er;
 * Pfeile wandern über Tortenstücke/Kacheln; 1–9 lösen Position 1–9 aus;
 * ein Buchstabe außerhalb eines Eingabefelds springt in die Suche. Tab bleibt
 * im Dialog. Die Android-Zurück-Taste (Tablet-App) geht wie Esc
 * (registriereZurueck). Fokus beim Öffnen in den Dialog, beim Schließen
 * zurück auf das vorige Element.
 */
export interface MangelKatalogDialogProps {
    offen: boolean;
    titel: string;
    onAuswahl: (a: KatalogAuswahl) => void;
    onSchliessen: () => void;
    startPfad?: string[];
    /** Ergänzung: Knopf „Freitext“ der Leiste; ohne Angabe schließt er den Dialog. */
    onFreitext?: () => void;
    /** Katalogsprache (28.09.); Standard deutsch. */
    sprache?: KatalogSprache;
}

const FOKUSSIERBAR = 'button:not([disabled]), input:not([disabled]), textarea, select, [tabindex]:not([tabindex="-1"])';
const PFEILE = ['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'];

const imEingabefeld = (ziel: EventTarget | null): boolean =>
    ziel instanceof HTMLInputElement ||
    ziel instanceof HTMLTextAreaElement ||
    ziel instanceof HTMLSelectElement ||
    (ziel instanceof HTMLElement && ziel.isContentEditable);

/** Tab/Umschalt+Tab am Rand springt ans andere Ende (Fokus bleibt im Dialog). */
function fokusFalle(e: KeyboardEvent, panel: HTMLElement): void {
    const alle = Array.from(panel.querySelectorAll<HTMLElement | SVGElement>(FOKUSSIERBAR)).filter(
        (el) => !el.closest('[aria-hidden="true"]') && el.getClientRects().length > 0,
    );
    if (!alle.length) return;
    const erstes = alle[0];
    const letztes = alle[alle.length - 1];
    const aktiv = document.activeElement;
    const drin = aktiv instanceof Element && panel.contains(aktiv) && aktiv !== panel;
    if (e.shiftKey && (!drin || aktiv === erstes)) {
        e.preventDefault();
        letztes.focus();
    } else if (!e.shiftKey && (!drin || aktiv === letztes)) {
        e.preventDefault();
        erstes.focus();
    }
}

const DialogOffen: React.FC<Omit<MangelKatalogDialogProps, 'offen'>> = ({ titel, onAuswahl, onSchliessen, startPfad, onFreitext, sprache = 'de' }) => {
    const flaecheRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const steuerung = useRef<KatalogSteuerung | null>(null);
    const titelId = useId();

    // Immer der neueste Rückruf, ohne Tastatur/Zurück-Taste neu anzumelden.
    const schliessenRef = useRef(onSchliessen);
    useEffect(() => {
        schliessenRef.current = onSchliessen;
    });

    const zurueck = useCallback(() => {
        if (!steuerung.current?.zurueck()) schliessenRef.current();
    }, []);

    // Fokus in den Dialog, beim Schließen zurück.
    useEffect(() => {
        const vorher = document.activeElement;
        panelRef.current?.focus({ preventScroll: true });
        return () => {
            if ((vorher instanceof HTMLElement || vorher instanceof SVGElement) && vorher.isConnected) vorher.focus({ preventScroll: true });
        };
    }, []);

    // Android-Zurück (nur in der App wirksam): wie Esc.
    useEffect(() => registriereZurueck(zurueck, () => flaecheRef.current), [zurueck]);

    // Tastatur am Dokument: nach einem Ansichtswechsel liegt der Fokus kurz auf body.
    useEffect(() => {
        const taste = (e: KeyboardEvent) => {
            const panel = panelRef.current;
            if (!panel || e.defaultPrevented || e.isComposing) return;
            if (e.key === 'Tab') {
                fokusFalle(e, panel);
                return;
            }
            const aktion = tasteDeuten(e, imEingabefeld(e.target));
            if (!aktion) {
                // Pfeil ohne Fokus auf einem Platz (gerade geöffnet): auf den ersten Platz.
                const aufPlatz = e.target instanceof Element && !!e.target.closest('[data-platz], [data-raster]');
                if (PFEILE.includes(e.key) && !aufPlatz && !imEingabefeld(e.target)) {
                    const erster = panel.querySelector<HTMLElement | SVGElement>('[data-platz]');
                    if (erster) {
                        e.preventDefault();
                        erster.focus();
                    }
                }
                return;
            }
            e.preventDefault();
            if (aktion.typ === 'zurueck') zurueck();
            else if (aktion.typ === 'position') steuerung.current?.position?.(aktion.n);
            else steuerung.current?.suche?.(aktion.text);
        };
        document.addEventListener('keydown', taste);
        return () => document.removeEventListener('keydown', taste);
    }, [zurueck]);

    const inhalt = (
        <div
            ref={flaecheRef}
            className="fixed inset-0 z-[150] flex items-center justify-center bg-stone-950/50 p-3"
            onMouseDown={(e) => {
                if (e.target === e.currentTarget) onSchliessen();
            }}
        >
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titelId}
                tabIndex={-1}
                style={{ outline: 'none' }}
                className="flex h-[min(92dvh,640px)] w-[min(96vw,440px)] flex-col overflow-hidden rounded-2xl bg-stone-50 shadow-xl"
            >
                <div className="flex shrink-0 items-center gap-2 border-b border-stone-200 bg-white py-1 pl-4 pr-1">
                    <h2 id={titelId} className="min-w-0 flex-1 truncate text-[15px] font-semibold text-stone-900">
                        {titel}
                    </h2>
                    <button
                        type="button"
                        onClick={onSchliessen}
                        aria-label={katalogTexte(sprache).schliessen}
                        className="flex min-h-11 min-w-11 items-center justify-center rounded-lg text-stone-600 hover:bg-stone-100 active:bg-stone-200"
                    >
                        <X size={20} aria-hidden="true" />
                    </button>
                </div>
                <div className="flex min-h-0 flex-1 flex-col">
                    <MangelKatalogAuswahl
                        darstellung="dialog"
                        steuerungRef={steuerung}
                        startPfad={startPfad}
                        onAuswahl={onAuswahl}
                        onVerlassen={onSchliessen}
                        onFreitext={onFreitext ?? onSchliessen}
                        sprache={sprache}
                    />
                </div>
            </div>
        </div>
    );

    // Portal an body (ein Vorfahr mit transform verschöbe sonst `fixed`); ohne DOM (Server-Markup) inline.
    return typeof document === 'undefined' ? inhalt : createPortal(inhalt, document.body);
};

export const MangelKatalogDialog: React.FC<MangelKatalogDialogProps> = ({ offen, ...rest }) =>
    offen ? <DialogOffen {...rest} /> : null;
